import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, Lock, Eye, Database, Globe, Mail, Server, Trash2, UserCheck, Bell, Cookie } from 'lucide-react';

export default function PrivacyPolicy() {
  const lastUpdated = "April 4, 2026";

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #0A0A1B 0%, #0D0D1F 100%)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl border-b" style={{ background: 'rgba(10, 10, 27, 0.8)', borderColor: 'rgba(255,255,255,0.05)' }}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white hover:opacity-80 transition-opacity">
            <ArrowLeft className="h-5 w-5" />
            <span className="font-semibold">Back to Privluxe</span>
          </Link>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5" style={{ color: '#7C3AED' }} />
            <span className="text-sm" style={{ color: 'rgba(229,231,235,0.6)' }}>Legal</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(34,211,238,0.2) 0%, rgba(124,58,237,0.2) 100%)', border: '1px solid rgba(34,211,238,0.3)' }}>
            <Lock className="h-8 w-8" style={{ color: '#22D3EE' }} />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">Privacy Policy</h1>
          <p style={{ color: 'rgba(229,231,235,0.6)' }}>Last updated: {lastUpdated}</p>
        </div>

        {/* Trust Message */}
        <div className="p-6 rounded-2xl mb-12" style={{ background: 'linear-gradient(135deg, rgba(34,211,238,0.1) 0%, rgba(124,58,237,0.1) 100%)', border: '1px solid rgba(34,211,238,0.2)' }}>
          <p className="text-lg text-center" style={{ color: '#E5E7EB' }}>
            <span style={{ color: '#22D3EE' }}>Messages are protected with advanced security protocols.</span><br />
            We do not access your personal conversations.<br />
            <span style={{ color: 'rgba(229,231,235,0.5)' }}>Your privacy is your power.</span>
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-12">
          {/* Section 1 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(124,58,237,0.2)' }}>
                <Eye className="h-5 w-5" style={{ color: '#A78BFA' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">1. Overview</h2>
            </div>
            <div className="pl-13 space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>Your privacy is central to our design. This Privacy Policy explains what information we collect, how we use it, how we protect it, and your rights regarding your personal data.</p>
              <p>Privluxe ("we", "us", "our") is committed to protecting the privacy of our users ("you", "your"). This policy applies to all users of the Privluxe platform and services.</p>
              <p>By using Privluxe, you consent to the data practices described in this policy. If you do not agree with our practices, please do not use our services.</p>
            </div>
          </section>

          {/* Section 2 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(34,211,238,0.2)' }}>
                <Database className="h-5 w-5" style={{ color: '#22D3EE' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">2. Information We Collect</h2>
            </div>
            <div className="pl-13 space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p><strong className="text-white">a) Account Information</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Phone number (for account verification and authentication)</li>
                <li>Email address (optional, for notifications and recovery)</li>
                <li>Username (your chosen display name)</li>
                <li>Profile picture (optional)</li>
                <li>Password (stored in encrypted/hashed form)</li>
              </ul>

              <p><strong className="text-white">b) Communication Data</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Messages are processed for delivery to intended recipients</li>
                <li>We do NOT actively read or monitor personal conversations</li>
                <li>Message metadata (timestamps, delivery status) may be stored temporarily</li>
                <li>Media files (images, videos, voice notes) are stored securely until delivered or deleted</li>
                <li>View-once media is automatically deleted after viewing</li>
              </ul>

              <p><strong className="text-white">c) Device & Technical Information</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Browser type and version</li>
                <li>Device type and operating system</li>
                <li>IP address (for security and fraud prevention)</li>
                <li>Approximate location (country/region level only)</li>
                <li>Session information and access times</li>
              </ul>

              <p><strong className="text-white">d) Usage Information</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Feature usage patterns (for service improvement)</li>
                <li>Error logs and crash reports</li>
                <li>Performance metrics</li>
              </ul>
            </div>
          </section>

          {/* Section 3 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(34,197,94,0.2)' }}>
                <Server className="h-5 w-5" style={{ color: '#22C55E' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">3. How We Use Your Information</h2>
            </div>
            <div className="pl-13 space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>We use the information we collect to:</p>
              <p><strong className="text-white">Service Operation:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Create and manage your account</li>
                <li>Deliver messages and media to intended recipients</li>
                <li>Enable voice and video calling features</li>
                <li>Process and complete your requests</li>
                <li>Provide customer support</li>
              </ul>

              <p><strong className="text-white">Security & Safety:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Verify user identity and prevent unauthorized access</li>
                <li>Detect and prevent fraud, abuse, and security threats</li>
                <li>Enforce our Terms of Service and policies</li>
                <li>Protect the safety of our users and third parties</li>
              </ul>

              <p><strong className="text-white">Improvement & Development:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Analyze usage patterns to improve our services</li>
                <li>Develop new features and functionality</li>
                <li>Fix bugs and optimize performance</li>
                <li>Conduct research and analytics (using anonymized data)</li>
              </ul>

              <p><strong className="text-white">Legal Compliance:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Comply with applicable laws and regulations</li>
                <li>Respond to legal requests and court orders</li>
                <li>Establish, exercise, or defend legal claims</li>
              </ul>
            </div>
          </section>

          {/* Section 4 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(245,158,11,0.2)' }}>
                <Lock className="h-5 w-5" style={{ color: '#F59E0B' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">4. Security Measures</h2>
            </div>
            <div className="pl-13 space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>We implement comprehensive security measures to protect your data:</p>
              <p><strong className="text-white">Technical Safeguards:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>End-to-end encryption for messages and media</li>
                <li>TLS/SSL encryption for data in transit</li>
                <li>Encryption at rest for stored data</li>
                <li>Secure authentication mechanisms (OTP, 2FA)</li>
                <li>Regular security audits and penetration testing</li>
              </ul>

              <p><strong className="text-white">Organizational Safeguards:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Strict access controls and authentication for employees</li>
                <li>Regular security training for staff</li>
                <li>Incident response procedures</li>
                <li>Data minimization practices</li>
              </ul>

              <div className="p-4 rounded-lg mt-4" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
                <p className="text-sm" style={{ color: '#FCA5A5' }}>
                  <strong>Important:</strong> While we implement industry-leading security measures, no system is completely immune to risk. You are responsible for protecting your account credentials and the security of your devices.
                </p>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.2)' }}>
                <Globe className="h-5 w-5" style={{ color: '#EF4444' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">5. Data Sharing</h2>
            </div>
            <div className="pl-13 space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <div className="p-4 rounded-lg" style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)' }}>
                <p className="font-semibold" style={{ color: '#22C55E' }}>We do NOT sell your personal data.</p>
              </div>

              <p className="mt-4">We may share your information only in the following circumstances:</p>

              <p><strong className="text-white">With Your Consent:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>When you explicitly agree to share information</li>
                <li>When you use features that inherently involve sharing (e.g., messaging another user)</li>
              </ul>

              <p><strong className="text-white">Service Providers:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Cloud hosting and infrastructure providers</li>
                <li>Analytics services (using anonymized data)</li>
                <li>Communication services (SMS, email providers)</li>
                <li>Payment processors (if applicable)</li>
              </ul>

              <p><strong className="text-white">Legal Requirements:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>When required by law, regulation, or legal process</li>
                <li>To respond to valid government requests</li>
                <li>To enforce our Terms or protect our rights</li>
                <li>To prevent fraud, harm, or illegal activities</li>
              </ul>

              <p><strong className="text-white">Business Transfers:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>In connection with a merger, acquisition, or sale of assets</li>
                <li>Your information may be transferred as part of such transaction</li>
              </ul>
            </div>
          </section>

          {/* Section 6 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(124,58,237,0.2)' }}>
                <Trash2 className="h-5 w-5" style={{ color: '#A78BFA' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">6. Data Retention</h2>
            </div>
            <div className="pl-13 space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>We retain your data only as long as necessary for the purposes described in this policy:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong className="text-white">Account Data:</strong> Retained while your account is active and for a reasonable period thereafter</li>
                <li><strong className="text-white">Messages:</strong> Stored until delivered, deleted by users, or automatically purged based on retention settings</li>
                <li><strong className="text-white">View-Once Media:</strong> Automatically deleted after viewing</li>
                <li><strong className="text-white">Logs & Analytics:</strong> Retained for up to 90 days for security and debugging purposes</li>
                <li><strong className="text-white">Backup Data:</strong> May be retained for up to 30 days for disaster recovery</li>
              </ul>

              <p>We may retain certain information longer if required by law or for legitimate business purposes (e.g., fraud prevention, legal claims).</p>
            </div>
          </section>

          {/* Section 7 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(34,211,238,0.2)' }}>
                <UserCheck className="h-5 w-5" style={{ color: '#22D3EE' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">7. Your Rights & Controls</h2>
            </div>
            <div className="pl-13 space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>You have the following rights regarding your personal data:</p>

              <p><strong className="text-white">Access & Portability:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Request a copy of your personal data</li>
                <li>Export your data in a portable format</li>
              </ul>

              <p><strong className="text-white">Correction & Updates:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Update your profile information at any time</li>
                <li>Correct inaccurate personal data</li>
              </ul>

              <p><strong className="text-white">Deletion:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Delete individual messages and conversations</li>
                <li>Request deletion of your account and associated data</li>
                <li>Use disappearing messages feature for automatic deletion</li>
              </ul>

              <p><strong className="text-white">Restriction & Objection:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Restrict certain processing activities</li>
                <li>Object to processing based on legitimate interests</li>
                <li>Opt out of marketing communications</li>
              </ul>

              <p>To exercise these rights, contact us at privacy@privluxe.com</p>
            </div>
          </section>

          {/* Section 8 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(34,197,94,0.2)' }}>
                <Cookie className="h-5 w-5" style={{ color: '#22C55E' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">8. Cookies & Tracking</h2>
            </div>
            <div className="pl-13 space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>We use cookies and similar technologies for:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong className="text-white">Essential Cookies:</strong> Required for the platform to function (authentication, security)</li>
                <li><strong className="text-white">Analytics Cookies:</strong> Help us understand how users interact with our service</li>
                <li><strong className="text-white">Preference Cookies:</strong> Remember your settings and preferences</li>
              </ul>
              <p>You can manage cookie preferences through your browser settings. Note that disabling essential cookies may affect platform functionality.</p>
              <p>For more details, see our <Link to="/cookies" className="underline" style={{ color: '#22D3EE' }}>Cookie Policy</Link>.</p>
            </div>
          </section>

          {/* Section 9 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(245,158,11,0.2)' }}>
                <Bell className="h-5 w-5" style={{ color: '#F59E0B' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">9. Policy Updates</h2>
            </div>
            <div className="pl-13 space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>We may update this Privacy Policy from time to time. When we make significant changes, we will:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Update the "Last Updated" date at the top of this policy</li>
                <li>Notify you through the platform or via email (for material changes)</li>
                <li>Provide a summary of key changes</li>
              </ul>
              <p>Your continued use of Privluxe after any changes indicates your acceptance of the updated policy.</p>
            </div>
          </section>

          {/* Section 10 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(124,58,237,0.2)' }}>
                <Mail className="h-5 w-5" style={{ color: '#A78BFA' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">10. Contact Us</h2>
            </div>
            <div className="pl-13 space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>If you have questions, concerns, or requests regarding this Privacy Policy or your personal data, please contact us:</p>
              <div className="p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)' }}>
                <p><strong className="text-white">Privluxe Privacy Team</strong></p>
                <p>Email: privacy@privluxe.com</p>
                <p>Website: www.privluxe.com/contact</p>
              </div>
              <p className="mt-4">We will respond to your request within 30 days.</p>
            </div>
          </section>
        </div>

        {/* Related Links */}
        <div className="mt-16 p-6 rounded-2xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <h3 className="text-lg font-semibold text-white mb-4">Related Documents</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/terms" className="p-4 rounded-xl text-center transition-all hover:scale-105" style={{ background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.2)' }}>
              <Shield className="h-6 w-6 mx-auto mb-2" style={{ color: '#A78BFA' }} />
              <span className="text-sm text-white">Terms & Conditions</span>
            </Link>
            <Link to="/acceptable-use" className="p-4 rounded-xl text-center transition-all hover:scale-105" style={{ background: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.2)' }}>
              <UserCheck className="h-6 w-6 mx-auto mb-2" style={{ color: '#22D3EE' }} />
              <span className="text-sm text-white">Acceptable Use</span>
            </Link>
            <Link to="/data-deletion" className="p-4 rounded-xl text-center transition-all hover:scale-105" style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)' }}>
              <Trash2 className="h-6 w-6 mx-auto mb-2" style={{ color: '#22C55E' }} />
              <span className="text-sm text-white">Data Deletion</span>
            </Link>
            <Link to="/law-enforcement" className="p-4 rounded-xl text-center transition-all hover:scale-105" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)' }}>
              <Globe className="h-6 w-6 mx-auto mb-2" style={{ color: '#F59E0B' }} />
              <span className="text-sm text-white">Law Enforcement</span>
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t mt-16" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          <p style={{ color: 'rgba(229,231,235,0.4)' }}>© 2026 Privluxe. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
